import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { TrendingUp, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../utils/apiBase";
const API = `${BACKEND_URL}/api`;

const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading', 'ready', 'error'
  const [analysisId, setAnalysisId] = useState(null);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  const verifySession = useCallback(async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Not logged in');
        setStatus('error');
        return;
      }

      const response = await axios.get(
        `${API}/checkout/verify-session/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data;

      if (data.status === 'paid' && data.analysis_id) {
        // Deep Analysis is ready!
        setAnalysisId(data.analysis_id);
        setStatus('ready');
      } else if (data.status === 'paid' && !data.analysis_id) {
        // Payment successful but Deep Analysis still generating
        // Retry after 3 seconds (max 20 retries = 60 seconds)
        if (retryCount < 20) {
          setTimeout(() => setRetryCount(retryCount + 1), 3000);
        } else {
          setError('Your analysis is taking longer than usual - this is normal for complex cases. We will finish it automatically and it will be available in your dashboard shortly. If this does not resolve within a few minutes, reach us at support@finance-vitals.com - we will fix it.');
          setStatus('delayed');
        }
      } else {
        setError('Payment not completed');
        setStatus('error');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError(err.response?.data?.detail || 'Failed to verify payment');
      setStatus('error');
    }
  }, [retryCount]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setError('No session ID found');
      setStatus('error');
      return;
    }

    verifySession(sessionId);
  }, [searchParams, retryCount, verifySession]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-6"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Processing Your Payment</h2>
          <p className="text-gray-600">
            {retryCount > 10 
              ? 'Your analysis is taking a bit longer than usual — this is normal for complex cases.'
              : retryCount > 0 
              ? 'Generating your Deep Analysis... This may take up to 60 seconds.'
              : 'Verifying your payment and preparing your Deep Analysis...'}
          </p>
          <div className="mt-6">
            <Clock className="w-6 h-6 text-blue-600 mx-auto animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error' || status === 'delayed') {
    const isDelayed = status === 'delayed';
    return (
      <div className={`min-h-screen bg-gradient-to-br ${isDelayed ? 'from-yellow-50 to-orange-100' : 'from-red-50 to-orange-100'} flex items-center justify-center p-4`}>
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="flex justify-center mb-6">
            <div className={`w-16 h-16 ${isDelayed ? 'bg-yellow-100' : 'bg-red-100'} rounded-full flex items-center justify-center`}>
              {isDelayed ? (
                <Clock className="w-10 h-10 text-yellow-600" />
              ) : (
                <AlertCircle className="w-10 h-10 text-red-600" />
              )}
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">
            {isDelayed ? 'Analysis In Progress' : 'Payment Error'}
          </h2>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <div className="flex flex-col space-y-3">
            <Link
              to="/individual"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-center transition-colors"
            >
              Go to Dashboard
            </Link>
            <Link
              to="/free-checkup"
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg text-center transition-colors"
            >
              Back to Checkup
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'delayed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
              <Clock className="w-10 h-10 text-yellow-600" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">Analysis In Progress</h2>
          <p className="text-gray-600 text-center mb-6">{error}</p>
          <div className="flex flex-col space-y-3">
            <Link
              to="/individual"
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg text-center transition-colors"
            >
              Go to Dashboard
            </Link>
            <Link
              to="/free-checkup"
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg text-center transition-colors"
            >
              Back to Checkup
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // status === 'ready'
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-12 h-12 text-green-600" />
          </div>
        </div>

        {/* Success Message */}
        <h1 className="text-3xl font-bold text-gray-900 text-center mb-3">
          Payment Successful!
        </h1>
        <p className="text-lg text-gray-600 text-center mb-8">
          Your Deep Analysis is ready
        </p>

        {/* What's Next */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">What&apos;s included:</h2>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Comprehensive risk assessment and scoring interpretation</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Prioritized action plans (30-day, 90-day, long-term)</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Opportunities for improvement with expected impact</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Tailored service recommendations</span>
            </li>
          </ul>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <button
            onClick={() => navigate(`/deep-analysis/${analysisId}`)}
            className="flex-1 px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold rounded-lg transition-all transform hover:scale-105 shadow-lg"
          >
            View Your Deep Analysis →
          </button>
          <Link
            to="/individual"
            className="flex-1 px-6 py-4 border-2 border-gray-300 hover:border-gray-400 text-gray-700 font-semibold rounded-lg text-center transition-colors"
          >
            Go to Dashboard
          </Link>
        </div>

        {/* Refund & Support Policy */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <p className="text-sm text-gray-700 text-center">
            <strong>Need help?</strong> If a verified technical issue prevented you from accessing your analysis, contact us within 7 days for a refund. Refunds aren't available based on dissatisfaction with the analysis or its results. Reach us at{' '}
            <a href="mailto:support@finance-vitals.com" className="text-blue-600 hover:underline" data-testid="checkout-support-email-link">
              support@finance-vitals.com
            </a>
          </p>
        </div>

        {/* Receipt Note */}
        <p className="text-xs text-gray-500 text-center">
          A receipt has been sent to your email address.
        </p>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
